import { useState } from 'react';
import {
    useAccountDeletionStatus,
    useRequestAccountDeletion,
    useCancelAccountDeletion,
} from '@/api/hooks/useAccountDeletion';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/Dialog';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { getErrorMessage } from '@/utils/errorHandler';
import { toast } from 'sonner';
import {
    TrashIcon,
    ExclamationTriangleIcon,
    XMarkIcon,
    ArrowDownTrayIcon,
    ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
}

export function DeleteAccountSection() {
    const { data: status, isLoading } = useAccountDeletionStatus();
    const requestDeletion = useRequestAccountDeletion();
    const cancelDeletion = useCancelAccountDeletion();

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [error, setError] = useState<string | null>(null);

    async function handleRequestDeletion() {
        setError(null);
        try {
            await requestDeletion.mutateAsync();
            toast.success('Confirmation email sent. Please check your inbox.');
            setShowConfirmDialog(false);
        } catch (err) {
            setError(getErrorMessage(err));
        }
    }

    async function handleCancelDeletion() {
        try {
            await cancelDeletion.mutateAsync();
            toast.success('Account deletion cancelled.');
        } catch (err) {
            toast.error(getErrorMessage(err));
        }
    }

    if (isLoading) {
        return (
            <div className="rounded-lg border border-red-200 bg-white p-6 dark:border-red-800/50 dark:bg-secondary">
                <div className="animate-pulse">
                    <div className="h-6 w-32 rounded-sm bg-gray-200 dark:bg-dark" />
                </div>
            </div>
        );
    }

    const deletionStatus = status?.status;
    const scheduledDate = status?.scheduledDeletionDate;

    return (
        <div className="rounded-lg border border-red-200 bg-white p-6 dark:border-red-800/50 dark:bg-secondary">
            <div className="flex items-center gap-3">
                <div className="rounded-lg bg-red-100 p-2 dark:bg-red-900/30">
                    <TrashIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Delete Account
                </h2>
            </div>

            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Permanently delete your account and all associated data. This action cannot be undone.
            </p>

            {deletionStatus === 'confirmed' && scheduledDate && (
                <div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                        <div className="min-w-0 flex-1">
                            <p className="font-medium text-red-800 dark:text-red-300">
                                Account scheduled for deletion
                            </p>
                            <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                                Your account will be permanently deleted on{' '}
                                <span className="font-medium">{formatDate(scheduledDate)}</span>
                            </p>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleCancelDeletion}
                            isLoading={cancelDeletion.isPending}
                            className="shrink-0 text-red-700 hover:bg-red-100 dark:text-red-400 dark:hover:bg-red-900/30"
                        >
                            <XMarkIcon className="mr-1 h-4 w-4" />
                            Cancel
                        </Button>
                    </div>
                </div>
            )}

            {deletionStatus === 'awaiting_confirmation' && (
                <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                        <div>
                            <p className="font-medium text-amber-800 dark:text-amber-300">
                                Confirmation email sent
                            </p>
                            <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                                Please check your inbox and click the confirmation link to proceed with account deletion.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {!deletionStatus && (
                <div className="mt-4">
                    <Button variant="danger" onClick={() => setShowConfirmDialog(true)}>
                        Delete Account
                    </Button>
                </div>
            )}

            <Dialog
                open={showConfirmDialog}
                onClose={() => {
                    setShowConfirmDialog(false);
                    setError(null);
                }}
                title="Delete Account"
                size="sm"
            >
                <div className="space-y-4">
                    {/* Backup suggestion */}
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-900/20">
                        <div className="flex items-start gap-3">
                            <ArrowDownTrayIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                            <div>
                                <p className="font-medium text-amber-800 dark:text-amber-300">
                                    Export your data first
                                </p>
                                <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">
                                    Before deleting your account, consider
                                    downloading a backup of your data.
                                </p>
                                <Link
                                    to="/data"
                                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-amber-700 hover:underline dark:text-amber-400"
                                >
                                    Go to Data page
                                    <ArrowRightIcon className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/20">
                        <p className="font-medium text-red-800 dark:text-red-300">
                            Are you sure you want to delete your account?
                        </p>
                        <ul className="mt-2 list-disc pl-5 text-sm text-red-700 dark:text-red-400">
                            <li>All your data will be permanently deleted</li>
                            <li>You will have 7 days to cancel this request</li>
                            <li>After 7 days, this action cannot be undone</li>
                        </ul>
                    </div>

                    {error && <ErrorAlert message={error} />}

                    <div className="flex justify-end gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowConfirmDialog(false);
                                setError(null);
                            }}
                            disabled={requestDeletion.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleRequestDeletion}
                            isLoading={requestDeletion.isPending}
                        >
                            Yes, Delete My Account
                        </Button>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
